import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Edit3, X, Briefcase } from "lucide-react";
import { toast } from "sonner";

const STATUSES = [
    { v: "employed", l: "Employed" },
    { v: "self_employed", l: "Self-Employed" },
    { v: "unemployed", l: "Unemployed" },
    { v: "retired", l: "Retired" },
    { v: "disabled", l: "Disabled" },
    { v: "student", l: "Student" },
    { v: "unknown", l: "Unknown" },
];
const PAY = [
    { v: "weekly", l: "Weekly" },
    { v: "biweekly", l: "Biweekly" },
    { v: "semi_monthly", l: "Semi-monthly" },
    { v: "monthly", l: "Monthly" },
    { v: "annually", l: "Annually" },
    { v: "irregular", l: "Irregular" },
    { v: "unknown", l: "Unknown" },
];
const MARITAL = [
    { v: "single", l: "Single" },
    { v: "married", l: "Married" },
    { v: "separated", l: "Separated" },
    { v: "divorced", l: "Divorced" },
    { v: "widowed", l: "Widowed" },
    { v: "unknown", l: "Unknown" },
];

const blank = () => ({
    employment_status: "unknown", employer_name: "", job_title: "",
    annual_income: "", monthly_income: "", pay_frequency: "unknown",
    income_source: "", household_size: "", marital_status: "unknown",
    spouse_income_included: false, spouse_income: "", income_notes: "",
});

export default function ClientEmploymentTab({ client, onSaved }) {
    const [data, setData] = useState(null);
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(blank());
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const r = await api.get(`/clients/${client.id}/employment-income`);
            setData(r.data || blank());
        } catch (e) {
            // Endpoint may be slow/unreachable — never leave the tab spinning.
            console.error("employment load failed:", e);
            setData(blank());
        }
    }, [client.id]);

    useEffect(() => { load(); }, [load]);

    const startEdit = () => {
        setDraft({
            ...blank(),
            ...data,
            annual_income: data?.annual_income ?? "",
            monthly_income: data?.monthly_income ?? "",
            household_size: data?.household_size ?? "",
            spouse_income: data?.spouse_income ?? "",
            employer_name: data?.employer_name ?? "",
            job_title: data?.job_title ?? "",
            income_source: data?.income_source ?? "",
            income_notes: data?.income_notes ?? "",
        });
        setEditing(true);
    };

    const save = async () => {
        setBusy(true);
        try {
            // Coerce numerics — empty strings become null.
            const payload = {
                ...draft,
                annual_income: draft.annual_income === "" ? null : Number(draft.annual_income),
                monthly_income: draft.monthly_income === "" ? null : Number(draft.monthly_income),
                spouse_income: draft.spouse_income === "" ? null : Number(draft.spouse_income),
                household_size: draft.household_size === "" ? null : parseInt(draft.household_size, 10),
            };
            await api.put(`/clients/${client.id}/employment-income`, payload);
            toast.success("Employment / Income saved");
            setEditing(false);
            await load();
            onSaved && onSaved();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    if (!data) return <div className="text-sm text-muted-foreground p-5">Loading…</div>;

    return (
        <div className="bg-white border border-border rounded-xl p-5 shadow-sm" data-testid="client-employment-tab">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <Briefcase className="h-4 w-4 text-primary" />
                    <div className="text-sm font-medium text-gray-900">Employment &amp; Income</div>
                </div>
                {editing ? (
                    <div className="flex gap-1.5">
                        <Button size="sm" variant="outline" onClick={() => setEditing(false)} disabled={busy}><X className="h-3.5 w-3.5 mr-1" />Cancel</Button>
                        <Button size="sm" onClick={save} disabled={busy} data-testid="employment-save">{busy ? "Saving…" : "Save"}</Button>
                    </div>
                ) : (
                    <Button size="sm" variant="ghost" onClick={startEdit} data-testid="employment-edit"><Edit3 className="h-3.5 w-3.5 mr-1" />Edit</Button>
                )}
            </div>

            {editing ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <FieldSelect label="Employment Status" value={draft.employment_status} onChange={v => setDraft({ ...draft, employment_status: v })} options={STATUSES} testId="edit-emp-status" />
                    <FieldInput label="Employer Name" value={draft.employer_name} onChange={v => setDraft({ ...draft, employer_name: v })} testId="edit-employer" />
                    <FieldInput label="Job Title" value={draft.job_title} onChange={v => setDraft({ ...draft, job_title: v })} testId="edit-job-title" />
                    <FieldInput label="Annual Income ($)" type="number" value={draft.annual_income} onChange={v => setDraft({ ...draft, annual_income: v })} testId="edit-annual-income" />
                    <FieldInput label="Monthly Income ($)" type="number" value={draft.monthly_income} onChange={v => setDraft({ ...draft, monthly_income: v })} testId="edit-monthly-income" />
                    <FieldSelect label="Pay Frequency" value={draft.pay_frequency} onChange={v => setDraft({ ...draft, pay_frequency: v })} options={PAY} testId="edit-pay-frequency" />
                    <FieldInput label="Income Source" value={draft.income_source} onChange={v => setDraft({ ...draft, income_source: v })} testId="edit-income-source" />
                    <FieldInput label="Household Size" type="number" value={draft.household_size} onChange={v => setDraft({ ...draft, household_size: v })} testId="edit-household" />
                    <FieldSelect label="Marital Status" value={draft.marital_status} onChange={v => setDraft({ ...draft, marital_status: v })} options={MARITAL} testId="edit-marital" />
                    <label className="flex items-center gap-2 text-sm pt-6">
                        <input type="checkbox" checked={draft.spouse_income_included} onChange={e => setDraft({ ...draft, spouse_income_included: e.target.checked })} data-testid="edit-spouse-included" />
                        <span>Include spouse income</span>
                    </label>
                    <FieldInput label="Spouse Income ($)" type="number" value={draft.spouse_income} onChange={v => setDraft({ ...draft, spouse_income: v })} testId="edit-spouse-income" disabled={!draft.spouse_income_included} />
                    <div className="md:col-span-2 space-y-1.5">
                        <label className="text-xs font-medium text-gray-700">Income Notes</label>
                        <Textarea value={draft.income_notes} onChange={e => setDraft({ ...draft, income_notes: e.target.value })} rows={3} data-testid="edit-income-notes" />
                    </div>
                </div>
            ) : (
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                    <DataRow label="Employment Status" value={(STATUSES.find(s => s.v === data.employment_status) || {}).l} />
                    <DataRow label="Employer" value={data.employer_name} />
                    <DataRow label="Job Title" value={data.job_title} />
                    <DataRow label="Annual Income" value={data.annual_income != null ? `$${Number(data.annual_income).toLocaleString()}` : null} />
                    <DataRow label="Monthly Income" value={data.monthly_income != null ? `$${Number(data.monthly_income).toLocaleString()}` : null} />
                    <DataRow label="Pay Frequency" value={(PAY.find(p => p.v === data.pay_frequency) || {}).l} />
                    <DataRow label="Income Source" value={data.income_source} />
                    <DataRow label="Household Size" value={data.household_size != null ? String(data.household_size) : null} />
                    <DataRow label="Marital Status" value={(MARITAL.find(m => m.v === data.marital_status) || {}).l} />
                    <DataRow label="Spouse Income" value={data.spouse_income_included ? (data.spouse_income != null ? `$${Number(data.spouse_income).toLocaleString()}` : "—") : "Not included"} />
                    <DataRow label="Last Income Update" value={data.last_income_update ? new Date(data.last_income_update).toLocaleString() : null} full />
                    {data.income_notes && (
                        <div className="md:col-span-2 pt-2 mt-2 border-t border-border">
                            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Notes</div>
                            <div className="text-sm whitespace-pre-wrap text-gray-800">{data.income_notes}</div>
                        </div>
                    )}
                </dl>
            )}
        </div>
    );
}

function FieldInput({ label, value, onChange, type = "text", testId, disabled = false }) {
    return (
        <label className="block space-y-1">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <Input type={type} value={value} onChange={e => onChange(e.target.value)} disabled={disabled} data-testid={testId} />
        </label>
    );
}

function FieldSelect({ label, value, onChange, options, testId }) {
    return (
        <label className="block space-y-1">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <Select value={value} onValueChange={onChange}>
                <SelectTrigger data-testid={testId}><SelectValue /></SelectTrigger>
                <SelectContent>{options.map(o => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
            </Select>
        </label>
    );
}

function DataRow({ label, value, full = false }) {
    return (
        <div className={`flex items-start justify-between gap-3 ${full ? "md:col-span-2" : ""}`}>
            <dt className="text-xs uppercase tracking-wider text-muted-foreground pt-0.5">{label}</dt>
            <dd className="text-right text-gray-800">{value || "—"}</dd>
        </div>
    );
}
